import { useConstant } from '@src/hooks/useConstant';
import { useObservable } from '@src/hooks/useObservable';
import { useSubscription } from '@src/hooks/useSubscription';
import { useWsPaginationLimiter, wsWaitForAllPages, type LimitReachedChangeEvent } from '@src/pipes';
import type { ExecTypeEnum, MinimalSubscriptionResponse } from '@src/types';
import type { RequestStream } from '@src/types/RequestStream';
import { isArray, isEqual } from 'lodash-es';
import { useEffect, useMemo, useRef, useState } from 'react';
import { BehaviorSubject, ReplaySubject, asyncScheduler, type Observable } from 'rxjs';
import { distinctUntilChanged, map, throttleTime } from 'rxjs/operators';
import type { BlotterTableFilter, CompositePipeFunction, InitialFetchState } from './types';
import { useBlotterTable } from './useBlotterTable';
import type { UseBlotterTable, UseBlotterTableProps } from './useBlotterTable/types';
import type { UsePersistedBlotterTable } from './usePersistedBlotterTable';

export type { InitialFetchState } from './types';

const WS_REQUEST_THROTTLE_MS = 50;

export interface WebsocketRequest extends RequestStream {
  Throttle?: string;
  HideApiCalls?: boolean;

  // Drives a server-driven sort (recognized by Ava repo internals)
  // Managed by the `sort` property of useWsBlotterTable
  sort_by?: string;
  CoalesceExecs?: ExecTypeEnum[];
}

/**
 * Props for the useWsBlotterTable hook
 * @template W Type for the Websocket Request
 * @template TRowOutputType Type for the rows after the pipe
 * @template TRowInputType Optional Type for the input stream from the request, TRowOutputType by default
 *
 * (Note the Type order is backwards (TOutputType/TInputType) on purpose, so that TS auto-detects the type for the pipe)
 */
export type UseWsBlotterTableProps<
  W extends Omit<WebsocketRequest, 'sort_by'>,
  TRowOutputType extends object,
  TRowInputType = TRowOutputType,
> = Omit<UseBlotterTableProps<TRowOutputType>, 'dataObservable' | 'errorObservable' | 'persistence'> & {
  /**
   * The request object to use for the websocket subscription. The hook will react to changes in this prop.
   * Include any filters directly on the request object using `removeEmptyFilters()`.
   * Pass `null` to hold the subscription until a request is ready.
   */
  request: W | null;
  /**
   * Optional function to transform the subscription response before applying the results to the grid.
   * Use it for applying additional filtering, adding properties, etc.
   */
  pipe?: CompositePipeFunction<TRowOutputType, TRowInputType>;

  /**
   * Whenever the request change, call this function to allow propagating the request up out of the hook.
   * Useful if there is a request outside of the blotter that should be kept in sync
   */
  onInnerRequestChanged?: (newRequest: W | null) => void;

  /** disables the ws subscription until true, useful for halting the subscription until mandatory filters are included for example */
  allowRequest?: boolean;

  startingRowLimit?: number;

  persistence?: UsePersistedBlotterTable;

  /** When true, pipes the subscription through wsWaitForAllPages so that downstream consumers
   *  (ag-grid) receive a single coalesced initial message only after all pages have been fetched.
   *  The ag-grid loading overlay is shown natively until data arrives. Defaults to false. */
  waitForAllPages?: boolean;
};

export type UseWsBlotterTable<
  TRowOutputType extends object,
  TRowInputType extends object = TRowOutputType,
> = UseBlotterTable<TRowOutputType> & {
  /** Pagination limiter related outputs. These provide an API for interacting with the blotter's pagination functionality and give insight into its current state. */
  paginationLimit: {
    /** Whether or not the pagination limit has been reached by the ws blotter table */
    limitReached?: boolean;
    /** If the limit has been reached, the last record we received will be returned here */
    lastRecord?: TRowInputType;
    /** If the limit has been reached, this number signifies how many records we have received to arrive at that conclusion */
    recordsReceived?: number;
    /** A function to call if you want to raise the pagination limit */
    raiseLimit: (raiseBy?: number) => void;
  };

  /** Tracks whether the initial data fetch (all pages) has completed, and how many records have been loaded so far. */
  initialFetchState: InitialFetchState;

  /** Sends a single record update to the blotter table observable */
  updateRecord: (record: TRowInputType) => void;
};

/**
 * Remove `undefined` or empty arrays when patching the current set of applied filters.
 * @param filters
 */
export function removeEmptyFilters<TFilterType extends BlotterTableFilter = BlotterTableFilter>(filters?: TFilterType) {
  const cleaned = { ...filters };
  for (const key in cleaned) {
    if (cleaned[key] == null || cleaned[key]?.length === 0) {
      delete cleaned[key];
    }
  }
  return cleaned as TFilterType;
}

/**
 * Construct a blotter table that is backed by a websocket-based rxjs subscription.
 * @param props Component props
 * @template TWebsocketRequestType Type for the Websocket Request
 * @template TRowOutputType Type for the rows after the pipe
 * @template TRowInputType Optional Type for the input stream from the request, TRowOutputType by default
 * @returns Blotter table object with the same API as useBlotterTable, but with additional methods for interacting with the websocket request
 */
export function useWsBlotterTable<
  TWebsocketRequestType extends WebsocketRequest,
  TRowOutputType extends object,
  TRowInputType extends object = TRowOutputType,
>({
  request,
  allowRequest = true,
  rowID,
  pipe,
  columns,
  groupableColumns,
  startingRowLimit,
  onInnerRequestChanged,
  waitForAllPages = false,
  ...props
}: UseWsBlotterTableProps<TWebsocketRequestType, TRowOutputType, TRowInputType>): UseWsBlotterTable<
  TRowOutputType,
  TRowInputType
> {
  const [activeRequest, setActiveRequest] = useState<TWebsocketRequestType | null>(null);

  const dataSubject = useConstant(new ReplaySubject<MinimalSubscriptionResponse<TRowInputType>>(1));

  const requestSubject = useConstant<BehaviorSubject<TWebsocketRequestType | null>>(
    new BehaviorSubject<TWebsocketRequestType | null>(request ?? null)
  );

  // Throttles updates to the activeRequest by WS_REQUEST_THROTTLE_MS
  const requestObservable = useObservable(
    () =>
      requestSubject.pipe(
        map(req => (allowRequest ? req : null)),
        distinctUntilChanged(isEqual),
        throttleTime(WS_REQUEST_THROTTLE_MS, asyncScheduler, {
          leading: false,
          trailing: true,
        })
      ),
    [allowRequest, requestSubject]
  );

  useEffect(() => {
    const subscription = requestObservable.subscribe(request => {
      setActiveRequest(request);
      // propagate the change out of the hook
      onInnerRequestChanged?.(request);
    });
    return () => subscription.unsubscribe();
  }, [requestObservable, onInnerRequestChanged]);

  const {
    data: subscription,
    error: errorObservable,
    nextPage,
  } = useSubscription(activeRequest, {
    loadAll: false,
    replay: false,
  });

  const [limitReachedState, setLimitReachedState] = useState<LimitReachedChangeEvent<TRowInputType> | undefined>(
    undefined
  );
  const wsPaginationLimiter = useWsPaginationLimiter<TRowInputType>({
    startingLimit: startingRowLimit ?? Infinity,
    nextPage,
    onLimitReachedChange: setLimitReachedState,
  });

  const paginationLimitedSubscription = useMemo(
    () => subscription.pipe(wsPaginationLimiter.wsPaginationLimiterInstance),
    [subscription, wsPaginationLimiter.wsPaginationLimiterInstance]
  );

  const [initialFetchState, setInitialFetchState] = useState<InitialFetchState>({
    isComplete: false,
    loadedCount: 0,
  });

  const mergedSubscription = useObservable(() => {
    const source = waitForAllPages
      ? paginationLimitedSubscription.pipe(wsWaitForAllPages<TRowInputType>({ onPageReceived: setInitialFetchState }))
      : paginationLimitedSubscription;

    return source.pipe(
      map(response => {
        dataSubject.next(response);
        return response;
      })
    );
  }, [paginationLimitedSubscription, dataSubject, waitForAllPages]);

  useEffect(() => {
    if (limitReachedState?.limitReached) {
      setInitialFetchState(prev => (prev.isComplete ? prev : { ...prev, isComplete: true }));
    }
  }, [limitReachedState?.limitReached]);

  // Allow parent to apply any kind of pipe to the data stream
  const pipedSubscription = useObservable(() => {
    return mergedSubscription.pipe(
      // `useSubscription` doesn't return a new observable when filters change etc, meaning
      // that the blotter table never knows when to show the loading overlay.
      // `map` (+ activeRequest in the deps array) creates a new observable when the request changes
      // so that the blotter is cleared out, and the loading overlay is shown.
      map(x => x),
      // type override needed since we want TRowInputType to be 'converted' to TRowOutputType regardless in the fallback
      pipe ?? (source => source as unknown as Observable<MinimalSubscriptionResponse<TRowOutputType>>)
    );
  }, [mergedSubscription, pipe]);

  // React to changes in the request prop
  const didInitialSubscription = useRef(false);
  const actualColumns = groupableColumns ?? columns;
  useEffect(() => {
    if (request == null) {
      if (requestSubject.value != null) {
        requestSubject.next(null);
      }
      return;
    }

    if (actualColumns != null) {
      const sortBy = request.sort_by;
      const newRequest: TWebsocketRequestType = {
        ...request,
        // Only change the sort parameters sent to the backend if we are doing infinite scrolling (which requires the backend
        // to know the sort). Otherwise always sort by what was specified in the request
        sort_by: isArray(sortBy) ? sortBy[0] : sortBy,
      };

      // Use isEqual to prevent unnecessary updates
      if (!didInitialSubscription.current || !isEqual(requestSubject.value, newRequest)) {
        requestSubject.next(newRequest);
        didInitialSubscription.current = true;
      }
    }
  }, [actualColumns, request, requestSubject]);

  const updateRecord = useMemo(
    () => (record: TRowInputType) => dataSubject.next({ data: [record], initial: false }),
    [dataSubject]
  );

  const blotterTable = useBlotterTable<TRowOutputType>({
    dataObservable: pipedSubscription,
    errorObservable,
    rowID,
    columns,
    groupableColumns,
    ...props,
    //TODO: Change the blotter table props to align with the useBlotterTable props
    // - the Input/Output RowType generic used with persistence does not (yet) line up with the UseBlotterTableProps types
  } as UseBlotterTableProps<TRowOutputType>);

  return useMemo(
    () =>
      ({
        ...blotterTable,
        paginationLimit: {
          ...limitReachedState,
          raiseLimit: wsPaginationLimiter.raiseLimit,
        },
        initialFetchState,
        updateRecord,
      }) satisfies UseWsBlotterTable<TRowOutputType, TRowInputType>,
    [blotterTable, limitReachedState, wsPaginationLimiter.raiseLimit, initialFetchState, updateRecord]
  );
}
